import { useCallback, useState } from 'react';
import type { ApolloClient } from '@frameio/graphql-client';
import { useApolloClient } from '@frameio/graphql-client';
import type { TranscodeKeys } from '@frameio/shared/assets/types';
import { useActiveAccountId } from '@frameio/shared/contexts';
import {
  type DownloadableAsset,
  fetchAssetsForDownload,
  fetchFolderChildrenForDownload,
} from './fetchAssetsForDownload';
import { getTranscodeByKey } from './getTranscodeByKey';

export type DownloadItem = {
  id: string;
  filename: string;
  url: string;
  path: string;
  /* The size of the file in bytes. */
  filesize: number;
};

const DEFAULT_TRANSCODE_KEY = 'original';
const PAGE_SIZE = 50;

/**
 * Hook to get structured download URLs for assets and their nested children
 *
 * @param opts Configuration options
 * @param opts.transcodeKey Which transcode version to get URLs for
 * @returns Object containing fetchDownloadItems function, loading state, and error state
 */
export function useGetAssetDownloadItems() {
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const client = useApolloClient();
  const accountId = useActiveAccountId();

  const fetchAssetsByIds = useCallback(
    async (assetIds: string[]) => {
      const assets = await fetchAssetsForDownload(
        client,
        { accountId },
        assetIds,
        'network-only'
      );

      return assets ?? [];
    },
    [accountId, client]
  );

  const fetchDownloadItems = useCallback(
    async (
      assetIds: string | string[],
      transcodeKey: TranscodeKeys = DEFAULT_TRANSCODE_KEY
    ) => {
      const ids = Array.isArray(assetIds) ? assetIds : [assetIds];

      if (ids.length === 0) {
        return [];
      }

      setIsLoading(true);
      setIsError(false);

      try {
        const assets = await fetchAssetsByIds(ids);

        const results = await Promise.all(
          assets.map((asset) =>
            fetchAssetDownloadItems(client, asset, transcodeKey)
          )
        );

        return results.flat();
      } catch (error) {
        setIsError(true);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [client, fetchAssetsByIds]
  );

  return {
    fetchDownloadItems,
    isLoading,
    isError,
  };
}

/**
 * Fetches download items for a given asset
 * @param client - ApolloClient
 * @param asset - The asset to fetch download items for
 * @param transcodeKey - The transcode key to use
 * @param parentPath - The path to the parent asset
 * @returns An array of DownloadItem objects
 */
async function fetchAssetDownloadItems(
  client: ApolloClient<any>,
  asset: DownloadableAsset,
  transcodeKey: TranscodeKeys,
  parentPath = ''
): Promise<DownloadItem[]> {
  if (!asset) return [];

  const assetPath = `${parentPath}/${asset.name}`;

  if (asset.__typename === 'FolderAsset') {
    const childAssets = await fetchFolderChildren(client, asset.id);

    const childResults = await Promise.all(
      childAssets.map((childAsset) =>
        fetchAssetDownloadItems(client, childAsset, transcodeKey, assetPath)
      )
    );

    return childResults.flat();
  }

  const transcode = getTranscodeByKey(asset, transcodeKey);
  const downloadUrl = transcode?.downloadUrl;
  const filesize =
    transcode && 'filesizeInBytes' in transcode
      ? transcode.filesizeInBytes ?? 0
      : 0;

  if (!downloadUrl) {
    return [];
  }

  return [
    {
      id: asset.id,
      filename: asset.name,
      url: downloadUrl,
      path: assetPath,
      filesize,
    },
  ];
}

/**
 * Fetches all children from a folder (paginated)
 * @param client - ApolloClient
 * @param folderId - The ID of the folder to fetch children from
 * @param offset - The offset to fetch children from
 * @returns An array of DownloadableAsset objects
 */
async function fetchFolderChildren(
  client: ApolloClient<any>,
  folderId: string,
  offset = 0
): Promise<DownloadableAsset[]> {
  const matchingChildren = await fetchFolderChildrenForDownload(
    client,
    {},
    folderId,
    { first: PAGE_SIZE, afterOffset: offset, mode: 'OFFSET' }
  );
  const assets = matchingChildren?.nodes ?? [];
  const pageInfo = matchingChildren?.pageInfo;

  if (pageInfo?.hasNextPage) {
    const nextAssets = await fetchFolderChildren(
      client,
      folderId,
      pageInfo.endOffset ?? 0
    );
    return [...assets, ...nextAssets];
  }

  return assets;
}
